import {
  DragDropContext,
  Draggable,
  Droppable,
  type Direction,
  type DraggableChildrenFn,
  type DraggableProvided,
  type DraggableStyle,
  type DroppableProvided,
  type OnDragEndResponder,
} from '@hello-pangea/dnd';
import type { MutableRefObject } from 'react';
import {
  Children,
  cloneElement,
  isValidElement,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
} from 'react';
import styled, { useTheme } from 'styled-components';

import { NavTab } from './NavTab';
import {
  OverflowWrapper,
  RightItems,
  TabBorder,
  TabIndicator,
  TabListWrapper,
  TabsWrapper,
  TabWrapper,
} from './styles';
import { Tab } from './Tab';
import { TabButton } from './TabButton';
import { TabsContext } from './Tabs';

import type { TabSize } from '@src/components/Tabs/types';
import { TabAppearance } from '@src/components/Tabs/types';
import { findLast, isArray } from 'lodash-es';
import { defineMessages } from 'react-intl';
import { useMountedState } from 'react-use';
import { v1 as uuid } from 'uuid';
import { useCallbackRef, useConstant, useElementSize, useIntl } from '../../hooks';
import { modeColor } from '../../styles';
import type { BoxProps } from '../Core';
import { Icon, IconName } from '../Icons';
import { Popover } from '../Popover/Popover';
import { usePopoverState } from '../Popover/usePopoverState';

const messages = defineMessages({
  countMore: {
    defaultMessage: '{count} More',
    id: 'Tabs.countMore',
  },
});

export interface TabListProps extends BoxProps {
  children?: ReactNode;
  rightItems?: ReactNode;
  isBordered?: boolean;
  suppressOverflowList?: boolean;
  renderAddTab?: () => ReactNode;
  rowEndSpacing?: string;
}

// Shared between DraggableTab (hoisted out of TabList so memoization works) and
// applyChildProps (used for non-reorderable tabs). When DraggableTab lived inside
// TabList it closed over these values; as a module-level component they must be
// passed explicitly — keep one shared shape so the two call sites stay in sync.
interface SharedTabChildProps {
  isOnlyChild: boolean;
  allowClosingLastTab: boolean;
  appearance: TabAppearance;
  size: TabSize;
  itemRefs: MutableRefObject<HTMLElement[]>;
  onSelect: ((index: number) => void) | undefined;
  onRename: ((index: number, text: string) => void) | undefined;
  onCancel: ((index: number) => void) | undefined;
  onRemove: ((index: number) => void) | undefined;
  selectedIndex: number;
  isDragging?: boolean;
  closePopover?: () => void;
}

interface DraggableTabProps extends SharedTabChildProps {
  draggableProvided: DraggableProvided;
  child: ReactElement;
  index: number;
  isDragged: boolean;
  withinOverflowList?: boolean;
}

interface ApplyChildPropsArgs extends SharedTabChildProps {
  child: ReactElement;
  index: number;
  withinOverflowList?: boolean;
}

function applyChildProps({
  child,
  isOnlyChild,
  allowClosingLastTab,
  appearance,
  size,
  index,
  itemRefs,
  onSelect,
  onRename,
  onCancel,
  onRemove,
  selectedIndex,
  isDragging,
  withinOverflowList,
  closePopover,
}: ApplyChildPropsArgs): ReactNode {
  return cloneElement(child, {
    ...child.props,
    closable: isOnlyChild ? allowClosingLastTab && child.props.closable : child.props.closable,
    appearance,
    size,
    key: child.props.id ?? child.key ?? index,
    'data-testid': child.props['data-testid'] ?? `tab-${index}`,
    ref: (element: HTMLElement) => {
      if (element == null) {
        return;
      }
      itemRefs.current[index] = element;
      return element;
    },
    isDragging,
    onTouchEnd: withinOverflowList
      ? (e: ReactTouchEvent) => {
          if (child.type === NavTab) {
            onSelect?.(index);
            return;
          }
          e.preventDefault();
          onSelect?.(index);
          closePopover?.();
        }
      : undefined,
    onClick: (e: React.MouseEvent<Element, MouseEvent>) => {
      onSelect?.(index);
      closePopover?.();
      child.props.onClick?.(e);
    },
    onRename: (name: string) => onRename?.(index, name),
    onCancel: () => onCancel?.(index),
    onRemove: () => onRemove?.(index),
    isSelected: selectedIndex === index,
  });
}

const DraggableTab = memo(function DraggableTab({
  draggableProvided,
  isDragged,
  child,
  index,
  withinOverflowList,
  isOnlyChild,
  allowClosingLastTab,
  appearance,
  size,
  itemRefs,
  onSelect,
  onRename,
  onCancel,
  onRemove,
  selectedIndex,
  isDragging,
  closePopover,
}: DraggableTabProps) {
  const theme = useTheme();
  const draggingStyles = isDragged
    ? {
        background: theme.backgroundContent,
        boxShadow: `0 2px 10px ${modeColor(theme, 'hsla(0, 0%, 0%, 0.1)', theme.colors.black.dim)}`,
      }
    : {};
  const getItemStyle = (draggableStyle: DraggableStyle | undefined): CSSProperties => ({
    display: 'flex',
    userSelect: 'none',
    padding: withinOverflowList ? '0px 8px' : 0,
    background: withinOverflowList && selectedIndex === index ? theme.colors.gray['020'] : 'transparent',
    ...draggingStyles,
    ...draggableStyle,
  });

  return (
    <div
      ref={draggableProvided.innerRef}
      {...draggableProvided.draggableProps}
      {...draggableProvided.dragHandleProps}
      style={getItemStyle(draggableProvided.draggableProps.style)}
    >
      {applyChildProps({
        child,
        isOnlyChild,
        allowClosingLastTab,
        appearance,
        size,
        index,
        itemRefs,
        onSelect,
        onRename,
        onCancel,
        onRemove,
        selectedIndex,
        isDragging,
        withinOverflowList,
        closePopover,
      })}
    </div>
  );
});

const overflowButtonSize = 80;

export const TabList = styled(function TabList({
  children,
  rightItems,
  isBordered = false,
  suppressOverflowList = false,
  renderAddTab,
  rowEndSpacing,
  ...props
}: TabListProps) {
  const {
    selectedIndex,
    onSelect,
    onRename,
    onCancel,
    onRemove,
    onReorder,
    onAdd,
    showAddTab,
    allowClosingLastTab,
    appearance,
    size,
  } = useContext(TabsContext)!;
  const theme = useTheme();
  const itemRefs = useRef<HTMLElement[]>([]);
  const isMounted = useMountedState();
  const [translateXMap, setTranslateXMap] = useState<number[]>([]);
  const [widthMap, setWidthMap] = useState<number[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const numberOfChildren = Children.count(children);
  const { formatMessage } = useIntl();

  const [maxVisible, setMaxVisible] = useState<number>(numberOfChildren);

  const {
    elementRef: containerSizeObserverRef,
    size: { offsetWidth: containerWidth = 0 },
  } = useElementSize<HTMLDivElement>({ debounceWait: 100 });

  // This useEffect tracks mounted/unmounted children, and also maintains a ResizeObserver that
  // watches if a child has been resized (due to renaming a tab for example). If so, we need to update
  // the tab indicator width.
  useEffect(() => {
    if (itemRefs.current != null) {
      const observer = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          const widthMap = itemRefs.current.map(item => item.offsetWidth);
          const translateXMap = itemRefs.current.map(item => item.offsetLeft);
          if (isMounted()) {
            setWidthMap(widthMap);
            setTranslateXMap(translateXMap);
          }
        });
      });
      for (const itemRef of itemRefs.current) {
        if (itemRef) {
          observer.observe(itemRef);
        }
      }
      return () => observer.disconnect();
    }
  }, [children, isMounted, containerWidth]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    elementRef: rightItemsRef,
    size: { offsetWidth: rightItemsWidth = 0 },
  } = useElementSize<HTMLDivElement>({ debounceWait: 100 });

  useEffect(() => {
    // handle cases where tabs are rendered with empty array initially, we need an initial sensible maxVisible that renders all children
    // only after rendering first time we can measure the element sizes and subsequently calculate what fits and what does not
    if (maxVisible === 0) {
      setMaxVisible(numberOfChildren);
    }
  }, [numberOfChildren, maxVisible]);

  // When overflow is suppressed, always show every tab in the main row. This must not depend on
  // widthMap or visibility — those caused stale maxVisible (e.g. tab count grew while widthMap was
  // still empty, or isHidden returned before the suppress branch ran).
  useEffect(() => {
    if (suppressOverflowList) {
      setMaxVisible(numberOfChildren);
    }
  }, [suppressOverflowList, numberOfChildren]);

  useEffect(() => {
    if (containerRef.current && widthMap.length) {
      if (suppressOverflowList) {
        setMaxVisible(numberOfChildren);
        return;
      }

      // Use getComputedStyle to read any padding applied to TabListWrapper by callers (e.g. Blotters/styles.ts)
      const containerStyle = window.getComputedStyle(containerRef.current);
      const containerPaddingX = parseFloat(containerStyle.paddingLeft) + parseFloat(containerStyle.paddingRight);

      // TabsWrapper has its own horizontal padding for Underlined appearance that further reduces usable space
      const tabsWrapperPaddingX = appearance === TabAppearance.Underlined ? theme.spacing12 * 2 : 0;

      // The Droppable flex container uses gap between visible tabs for Underlined appearance
      const tabGap = appearance === TabAppearance.Underlined ? theme.spacing16 : 0;

      let availableWidth =
        containerWidth - containerPaddingX - tabsWrapperPaddingX - rightItemsWidth - (showAddTab ? 30 : 0);

      let maxVisible;
      for (maxVisible = 0; maxVisible < numberOfChildren; ++maxVisible) {
        // unfortunately not all tabs have an accurate width tracking, only those on the main tab bar are up to date in widthMap
        // those in the overflow are all sized to the widest tab as to align the close icon, so using 90 as an average
        const nextTabWidth = widthMap[maxVisible] || 90;
        // account for the gap before each tab after the first
        const gapWidth = maxVisible > 0 ? tabGap : 0;
        const tabTotalWidth = nextTabWidth + gapWidth;

        if (availableWidth - tabTotalWidth > overflowButtonSize) {
          availableWidth = availableWidth - tabTotalWidth;
          continue;
        } else if (maxVisible === numberOfChildren - 1 && tabTotalWidth < availableWidth) {
          // if only 1 tab not fitting check if it fits reserved space instead - i.e. don't show "1 more" if the underlying tab fits there
          continue;
        }
        break;
      }

      setMaxVisible(Math.max(maxVisible, 1)); // ensure at least 1 is visible
    }
  }, [
    containerRef,
    rightItemsWidth,
    containerWidth,
    widthMap,
    numberOfChildren,
    showAddTab,
    appearance,
    size,
    suppressOverflowList,
    theme,
  ]);

  const closePopoverRef = useRef<() => void>(() => {});
  // Stable listener for usePopoverState; reads latest close via ref because popover is created below.
  const handleClickOutside = useCallbackRef((e: MouseEvent | TouchEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      closePopoverRef.current();
    }
  });

  const popover = usePopoverState({
    usePortal: true,
    trigger: 'click',
    placement: 'bottom-end',
    isSmall: false,
    delay: undefined,
    onClickOutside: handleClickOutside,
  });

  useLayoutEffect(() => {
    closePopoverRef.current = popover.close;
  }, [popover.close]);

  // Ensure we are positioning TabIndicator on already calculated offset
  // of latest tab as on initial render translateXMap[selectedIndex] will be
  // undefined until useEffect calculating offsets and widths fill execute.
  const translateX = useMemo(
    () => findLast(translateXMap, v => v > 0, selectedIndex) || 0,
    [translateXMap, selectedIndex]
  );

  const droppableId = useConstant(`tab-list-${uuid()}`);
  const overflowDroppableId = useConstant(`overflow-list-${uuid()}`);

  const handleDragEnd: OnDragEndResponder<string> = useCallback(
    result => {
      setIsDragging(false);
      if (result?.destination) {
        const isSourceOnOverflow = result.source.droppableId === overflowDroppableId;
        const isDestinationOnOverflow = result.destination.droppableId === overflowDroppableId;
        const isMovingFromListToOverflow = !isSourceOnOverflow && isDestinationOnOverflow;

        const destinationIndex = isMovingFromListToOverflow ? result.destination.index - 1 : result.destination.index;

        if (isArray(children) && children[destinationIndex].props?.reorderable) {
          onReorder?.(result.source.index, destinationIndex);
        }
      }
    },
    [onReorder, children, overflowDroppableId]
  );

  const getListStyle = (listId: string): CSSProperties => {
    return {
      display: 'flex',
      flexDirection: listId === droppableId ? 'row' : 'column',
      gap: listId === droppableId && appearance === TabAppearance.Underlined ? theme.spacing16 : 0,
    };
  };

  const isOnlyChild = numberOfChildren === 1;
  const sharedTabChildProps: SharedTabChildProps = {
    isOnlyChild,
    allowClosingLastTab,
    appearance,
    size,
    itemRefs,
    onSelect,
    onRename,
    onCancel,
    onRemove,
    selectedIndex,
    isDragging,
    closePopover: popover.close,
  };

  // https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/reparenting.md
  const renderClone: DraggableChildrenFn = (provided, snapshot, rubric) => (
    <TabWrapper
      background="backgroundContent"
      boxShadow={`0 1px 5px ${theme.backgroundBody}`}
      appearance={appearance}
      size={size}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      ref={provided.innerRef}
    >
      {isArray(children) && children?.[rubric.source.index]?.props?.label}
    </TabWrapper>
  );

  const renderDroppable = (id: string, direction: Direction, from: number, to: number, withinOverflowList: boolean) => (
    <Droppable droppableId={id} direction={direction} renderClone={renderClone}>
      {(droppableProvided: DroppableProvided) => (
        <div ref={droppableProvided.innerRef} style={getListStyle(id)} {...droppableProvided.droppableProps}>
          {Children.map(children, (child, index) => {
            if (!isValidElement(child) || index < from || index >= to) {
              return null;
            }
            if (child.type === Tab || child.type === NavTab) {
              const draggableId = child.props.id || uuid();
              const isReorderable = child.props.reorderable;
              return isReorderable ? (
                <Draggable isDragDisabled={!isReorderable} key={draggableId} draggableId={draggableId} index={index}>
                  {(draggableProvided, snapshot) => (
                    <DraggableTab
                      {...sharedTabChildProps}
                      isDragged={snapshot.isDragging}
                      draggableProvided={draggableProvided}
                      child={child}
                      index={index}
                      withinOverflowList={withinOverflowList}
                    />
                  )}
                </Draggable>
              ) : (
                applyChildProps({
                  child,
                  ...sharedTabChildProps,
                  index,
                  withinOverflowList,
                })
              );
            }
            return child;
          })}
          {droppableProvided.placeholder}
        </div>
      )}
    </Droppable>
  );

  const overflowList =
    suppressOverflowList || maxVisible >= numberOfChildren ? null : (
      <Popover {...popover}>
        <TabButton
          appearance={appearance}
          size={size}
          label={formatMessage(messages.countMore, { count: numberOfChildren - maxVisible })}
          isSelected={popover.isOpen || selectedIndex >= maxVisible}
          suffix={<Icon icon={popover.isOpen ? IconName.ChevronUp : IconName.ChevronDown} />}
        />
        <OverflowWrapper>
          {renderDroppable(overflowDroppableId, 'vertical', maxVisible, numberOfChildren, true)}
        </OverflowWrapper>
      </Popover>
    );

  // if we are adding new tab and overflow list exists, we must expand it to show
  const lastAddedIndex = useRef(-1);
  const handleAdd = useCallback(() => {
    lastAddedIndex.current = numberOfChildren;
    onAdd?.();
  }, [numberOfChildren, onAdd]);
  useEffect(() => {
    if (maxVisible <= lastAddedIndex.current) {
      popover.open();
    }
    // exclude popover or else you won't be able to close it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfChildren]);

  const handleRef = useCallback(
    (el: HTMLDivElement | null) => {
      containerRef.current = el;
      containerSizeObserverRef.current = el;
    },
    [containerSizeObserverRef]
  );

  return (
    <TabListWrapper data-testid="tablist-wrapper" {...props} size={size} appearance={appearance} ref={handleRef}>
      <TabsWrapper data-testid="tabs-wrapper" appearance={appearance} rowEndSpacing={rowEndSpacing}>
        <DragDropContext onDragStart={() => setIsDragging(true)} onDragEnd={handleDragEnd}>
          {renderDroppable(droppableId, 'horizontal', 0, maxVisible, false)}
          {overflowList}
        </DragDropContext>
        {showAddTab &&
          (renderAddTab ? (
            renderAddTab()
          ) : (
            <TabButton
              appearance={appearance}
              size={size}
              onClick={handleAdd}
              suffix={<Icon icon={IconName.Plus} />}
              data-testid="add-tab-button"
            />
          ))}
        {rightItems && <RightItems ref={rightItemsRef}>{rightItems}</RightItems>}
      </TabsWrapper>
      {translateXMap.length > 0 && selectedIndex < maxVisible && (
        <TabIndicator
          appearance={appearance}
          data-testid="tab-indicator"
          isDragging={isDragging}
          translateX={translateX}
          width={widthMap[selectedIndex]}
        />
      )}
      <TabBorder isBordered={isBordered} />
    </TabListWrapper>
  );
})``;
